/**
 * Project API endpoints
 */
import axiosClient from './axiosClient';
import { 
  Project, ProjectWithDetails, ProjectSummary, CreateProjectData, UpdateProjectData 
} from '@/types';

export const projectAPI = {
  /**
   * Get all projects
   */
  getProjects: async (params?: { 
    organization_id?: string;
    team_id?: string;
    owner_id?: string;
    is_active?: boolean;
    is_public?: boolean;
    skip?: number; 
    limit?: number;
    order_by?: string;
    order_direction?: string;
  }): Promise<Project[]> => {
    const response = await axiosClient.get<Project[]>('/projects', { params });
    return response.data;
  },

  /**
   * Get project by ID
   */
  getProject: async (id: string): Promise<ProjectWithDetails> => {
    const response = await axiosClient.get<ProjectWithDetails>(`/projects/${id}`);
    return response.data;
  },

  /**
   * Create new project
   */
  createProject: async (data: CreateProjectData): Promise<Project> => {
    const response = await axiosClient.post<Project>('/projects', data);
    return response.data;
  },

  /**
   * Update project
   */
  updateProject: async (id: string, data: UpdateProjectData): Promise<Project> => {
    const response = await axiosClient.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  /**
   * Delete project
   */
  deleteProject: async (id: string): Promise<void> => {
    await axiosClient.delete(`/projects/${id}`);
  },

  /**
   * Get project summary with statistics
   */
  getProjectSummary: async (id: string): Promise<ProjectSummary> => {
    const response = await axiosClient.get<ProjectSummary>(`/projects/${id}/summary`);
    return response.data;
  },

  /**
   * Archive project
   */
  archiveProject: async (id: string): Promise<Project> => {
    const response = await axiosClient.post<Project>(`/projects/${id}/archive`);
    return response.data;
  },

  /**
   * Activate project
   */
  activateProject: async (id: string): Promise<Project> => {
    const response = await axiosClient.post<Project>(`/projects/${id}/activate`);
    return response.data;
  },

  /**
   * Assign team to project (adds all team members to project and adds project to team's project_ids)
   */
  assignTeamToProject: async (projectId: string, teamId: string): Promise<void> => {
    await axiosClient.post(`/projects/${projectId}/assign-team`, { team_id: teamId });
  },

  /**
   * Remove a team from a project (removes all team-based members of that team)
   */
  removeTeamFromProject: async (projectId: string, teamId: string): Promise<void> => {
    await axiosClient.delete(`/projects/${projectId}/remove-team/${teamId}`);
  },
};